import * as React from "react";
import Marquee from "react-marquee-slider";
import styles from "./styles.module.scss";
import { MemoMessageModal as MessageModal } from "../../modals/MessageModal";
import Dialog from "../../Dialog";
import { useQuery } from "@apollo/client";
import {
  EventBySlugQuery,
  GetApprovedMessagesQuery,
  GetApprovedMessagesQueryVariables,
} from "@src/@types/api.d";
import { GetApprovedMessages } from "@src/graphql/queries";
import { getCookie } from "cookies-next";
import {
  ApprovedMessage,
  ApprovedMessages,
} from "@src/components/BirthdayTemplate1/MarqueeComp";

type Props = {
  eventId: number;
  slugData: EventBySlugQuery | undefined;
};

const MarqueeComp: React.FC<Props> = ({ slugData, eventId }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [show, setShow] = React.useState<boolean>(false);
  const currentAccessToken = getCookie("tsl") as string;
  const [isMarqueePaused, setIsMarqueePaused] = React.useState(false);

  const pauseMarquee = () => setIsMarqueePaused(true);
  const resumeMarquee = () => setIsMarqueePaused(false);

  React.useEffect(() => {
    setShow(true);
  }, []);

  const { data: getApprovedMessagesData } = useQuery<
    GetApprovedMessagesQuery,
    GetApprovedMessagesQueryVariables
  >(GetApprovedMessages, {
    skip: !eventId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { eventId },
    pollInterval: 1000 * 60,
  });

  const approvedMessages = (
    getApprovedMessagesData?.getApprovedMessages || []
  ).filter((message): message is ApprovedMessage => message !== null);

  const messagesToDisplay = approvedMessages;

  // Helper function to ensure at least 6 entries
  const repeatEntries = (entries: ApprovedMessages): ApprovedMessage[] => {
    if (!entries || entries.length === 0) return [];
    if (entries.length >= 6) return entries;

    const length = entries.length;
    let repeated: ApprovedMessages = [];

    if (length === 1) {
      // Repeat single entry 6 times
      repeated = Array(6).fill(entries[0]);
    } else if (length === 2) {
      // Repeat the pair thrice
      repeated = [...entries, ...entries, ...entries];
    } else if (length === 3) {
      // Repeat each entry 2 times
      repeated = [...entries, ...entries];
    } else if (length === 4) {
      // Repeat each entry 2 times (total 8)
      repeated = [...entries, ...entries];
    } else if (length === 5) {
      // Repeat each entry 2 times (total 10)
      repeated = [...entries, ...entries];
    }

    return repeated;
  };

  if (!messagesToDisplay.length) return null;

  // Prepare even and odd indexed messages
  const evenMessages = repeatEntries(
    messagesToDisplay.filter((_, i) => i % 2 === 0)
  );
  const oddMessages = repeatEntries(
    messagesToDisplay.filter((_, i) => i % 2 !== 0)
  );

  return messagesToDisplay.length > 0 ? (
    <section className="py-2 bg-[rgba(236,228,237,1)]">
      {/* Top border frames */}
      <div
        className="flex mb-10 lg:mb-20 justify-center h-5 w-full relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/Frame (3).svg')`,
          backgroundSize: "cover",
          backgroundRepeat: "repeat-x",
          backgroundPosition: "top left",
        }}
      ></div>

      <h3 className="font-silver text-4xl sm:text-5xl capitalize text-center text-black my-10 lg:mb-0">
        Greetings
      </h3>
      <div>
        <div
          className={styles["home__marquee"]}
          onMouseEnter={pauseMarquee}
          onMouseLeave={resumeMarquee}
          onClick={pauseMarquee}
        >
          {/* First Marquee Row */}
          <div
            style={{
              height: "320px",
              maxWidth: "100vw",
              width: "100%",
              overflow: "visible",
            }}
          >
            <Marquee
              velocity={isMarqueePaused ? 0 : 33}
              direction="rtl"
              scatterRandomly={false}
              resetAfterTries={200}
              onInit={() => null}
              onFinish={() => null}
            >
              {evenMessages.map((item, index) => (
                <div
                  key={`quotes-${index}`}
                  className={styles["home__special-note-box"]}
                >
                  <div className={styles["home__special-note-card"]}>
                    <p
                      className={`${styles["home__special-note-text"]} font-gill`}
                    >
                      {item?.content ?? ""}
                    </p>
                  </div>
                </div>
              ))}
            </Marquee>
          </div>

          {/* Second Marquee Row */}
          {oddMessages.length > 0 && (
            <div
              style={{
                height: "320px",
                maxWidth: "100vw",
                width: "100%",
                overflow: "visible",
              }}
            >
              <Marquee
                velocity={isMarqueePaused ? 0 : 33}
                direction="rtl"
                scatterRandomly={false}
                resetAfterTries={200}
                onInit={() => null}
                onFinish={() => null}
              >
                {oddMessages.map((item, index) => (
                  <div
                    key={`quotes-2-${index}`}
                    className={styles["home__special-note-box"]}
                  >
                    <div className={styles["home__special-note-card"]}>
                      <p
                        className={`${styles["home__special-note-text"]} font-gill`}
                      >
                        {item?.content ?? ""}
                      </p>
                    </div>
                  </div>
                ))}
              </Marquee>
            </div>
          )}

          {/* Special Note Button */}
          {show && !!currentAccessToken && (
            <div>
              <Dialog
                trigger={
                  <div className="px-6 py-2 rounded transition duration-300 border border-black bg-transparent text-black text-xl font-sans hover:bg-black hover:text-[rgba(236,228,237,1)] uppercase">
                    Drop a special note
                  </div>
                }
                open={open}
                onOpenChange={setOpen}
              >
                <MessageModal
                  name={slugData?.eventBySlug?.name ?? ""}
                  eventId={eventId}
                  setOpen={setOpen}
                />
              </Dialog>
            </div>
          )}
        </div>
      </div>

      <div
        className="mt-10 lg:mt-20 flex justify-center h-5 w-full relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/Frame (3).svg')`,
          backgroundSize: "cover",
          backgroundRepeat: "repeat-x",
          backgroundPosition: "top left",
          transform: "scaleY(-1)",
        }}
      ></div>
    </section>
  ) : null;
};

export default MarqueeComp;
